<template>
  <div class="static-files-settings">
    <div class="font-medium mt-3 text-sm text-gray-700">
      External Files
    </div>
    <div class="text-xs text-gray-500 mb-4">
      Add CSS and JavaScript files to your site. You can upload new files or select from your existing assets.
    </div>
    
    <!-- Files loop section -->
    <div v-for="(file, idx) of orderedExtFile" :key="idx">
      <!-- Display section headers -->
      <div
        v-if="idx === 0"
        class="font-semibold text-xs text-gray-700 mt-2 ml-2"
      >
        <span class="bullet-dot mr-2"></span> Scripts
      </div>
      <div
        v-if="idx === this.filesCount['script']"
        class="font-semibold text-xs text-gray-700 mt-3 ml-2"
      >
        <span class="bullet-dot mr-2"></span>Styles
      </div>
      <div
        v-if="idx === this.filesCount['script'] + this.filesCount['style']"
        class="font-semibold text-xs text-gray-700 mt-3 ml-2"
      >
        <span class="bullet-dot mr-2"></span>Images
      </div>
      <div
        v-if="idx === this.filesCount['script'] + this.filesCount['style'] + this.filesCount['image']"
        class="font-semibold text-xs text-gray-700 mt-3 ml-2"
      >
        <span class="bullet-dot mr-2"></span>Videos
      </div>

      <!-- File input and options section -->
      <div class="flex items-center mt-2 w-full">
        <span class="mx-2">
          {{ getFileNumber(idx) }}:
        </span>
        <select
          :value="file.type"
          class="px-2 py-1 border border-gray-300 custom-focus rounded-md w-20 mr-2 text-sm"
          :disabled="!!file.asset_id"
          :class="{ 'bg-gray-100 cursor-not-allowed text-gray-500': !!file.asset_id }"
          @change="updateDepFile('type', $event.target.value, idx)"
        >
          <option value="script">script</option>
          <option value="style">style</option>
          <option value="image">image</option>
          <option value="video">video</option>
        </select>
        <input
          class="flex-1 px-3 py-2 border border-gray-300 rounded-md custom-focus h-8"
          type="text"
          :defaultValue="file.url"
          placeholder="url"
          @change="updateDepFile('url', $event.target.value, idx)"
          :disabled="file.url.startsWith('file:')"
        />
        <span
          v-if="file.type === 'image' || file.type === 'video'"
          class="ml-2 flex items-center"
        >
          <span
            class="bg-gray-100 px-2 py-1 rounded text-xs font-mono select-all"
            :title="`Example: :src=&quot;global.static['${file.url.replace('file:', '')}']&quot;`"
            style="cursor: pointer;"
            @click="() => { navigator.clipboard.writeText(`global.static['${file.url.replace('file:', '')}']`) }"
          >
            global.static['{{ file.url.replace('file:', '') }}']
          </span>
          <button
            class="ml-1 px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 text-xs"
            title="Copy resource name"
            @click="() => { navigator.clipboard.writeText(`global.static['${file.url.replace('file:', '')}']`) }"
            type="button"
          >
            Copy
          </button>
        </span>
        <span
          class="ml-2 cursor-pointer opacity-30 hover:opacity-95"
          v-if="file.url.startsWith('file:')"
          @click="handleEditFile(file)"
          :title="(file.type === 'image' || file.type === 'video' || file.is_global) ? 'View' : 'Edit'"
        >
          <font-awesome-icon :icon="(file.type === 'image' || file.type === 'video' || file.is_global) ? 'eye' : 'edit'" />
        </span>
        <span
          class="ml-2 cursor-pointer opacity-30 hover:opacity-95"
          @click="removeDepFile(idx)"
        >
          <font-awesome-icon icon="minus" />
        </span>

        <!-- Sorting buttons -->
        <div class="flex ml-2">
          <span class="cursor-pointer ml-2" @click="sortFile(-1, idx)">
            <font-awesome-icon icon="caret-up" />
          </span>
          <span class="cursor-pointer ml-2" @click="sortFile(1, idx)">
            <font-awesome-icon icon="caret-down" />
          </span>
        </div>
      </div>
      <div
        v-if="file.asset_id"
        class="ml-10 text-xs text-blue-600"
      >
        Global asset: {{ file.asset_name || 'Unnamed asset' }} (v{{ file.asset_version || 1 }})
        <button
          class="ml-2 px-2 py-1 text-xs rounded bg-blue-100 text-blue-700 hover:bg-blue-200"
          type="button"
          @click="openVersionPicker(file, idx)"
        >
          Choose version
        </button>
      </div>
      <div
        v-if="file.asset_version_note"
        class="ml-10 text-xs text-gray-500"
      >
        Notes: {{ file.asset_version_note }}
      </div>
    </div>

    <!-- New file input section -->
    <AssetSelector
      :newDep="newDep"
      @update:newDep="updateNewDep"
      @addDepFile="addDepFile"
    />

    <vue-final-modal v-model="showVersionPicker" :click-to-close="false">
      <div class="bg-white w-full max-w-3xl mx-auto my-16 rounded shadow">
        <div class="px-4 py-3 border-b flex items-center justify-between">
          <div>
            <div class="font-semibold text-lg">Select Asset Version</div>
            <div class="text-sm text-gray-500">
              Choose which version of <strong>{{ versionPickerAssetName }}</strong> should be used for this site.
            </div>
          </div>
          <button class="action-btn" @click="closeVersionPicker">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        <div class="px-4 py-4">
          <div v-if="versionPickerLoading" class="py-10 flex justify-center">
            <i class="fa-solid fa-spinner fa-spin text-2xl text-gray-500"></i>
          </div>
          <div v-else>
            <table class="table mb-0">
              <thead>
                <tr>
                  <th>Version</th>
                  <th>Size</th>
                  <th>Updated</th>
                  <th>Notes</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="version in versionPickerOptions" :key="version._id">
                  <td>
                    <span class="badge bg-secondary">v{{ version.version || 1 }}</span>
                    <span v-if="versionPickerMeta?.published_version === version.version" class="text-xs text-primary ms-2">(Default)</span>
                  </td>
                  <td>{{ formatFileSize(version.file_size) }}</td>
                  <td>{{ formatDate(version.createdAt) }}</td>
                  <td class="text-xs text-gray-500">
                    {{ version.version_note || '-' }}
                  </td>
                  <td class="text-end">
                    <button
                      class="btn btn-sm"
                      :class="selectedVersionId === version._id ? 'btn-primary' : 'btn-outline-primary'"
                      @click="selectedVersionId = version._id"
                    >
                      {{ selectedVersionId === version._id ? 'Selected' : 'Select' }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="px-4 py-3 border-t flex justify-end gap-2">
          <button class="btn btn-default" @click="closeVersionPicker">Cancel</button>
          <button class="btn btn-primary" :disabled="!selectedVersionId" @click="applySelectedVersion">
            Use Selected Version
          </button>
        </div>
      </div>
    </vue-final-modal>

    <vue-final-modal :key="editFile.key" v-model="showFileEdit" :click-to-close="false">
      <div class="bg-white w-xl h-xl mx-auto my-24 max-w-3xl">
        <div class="px-3 py-2">
          {{ (editFile.type === 'image' || editFile.type === 'video' || editFile.is_global) ? 'View' : 'Edit' }} {{ editFile.key }}
        </div>
        <hr />
        <div class="max-h-96 overflow-auto">
          <div v-if="editFile.type === 'image'" class="w-full h-full flex items-center justify-center p-3">
            <img :src="editFile.previewUrl" alt="Preview" class="max-w-full max-h-96 object-contain" />
          </div>
          <div v-else-if="editFile.type === 'video'" class="w-full h-full flex items-center justify-center p-3">
            <video :src="editFile.previewUrl" controls class="max-w-full max-h-96" />
          </div>
          <div v-else class="flex-1 min-h-0 h-xl" style="height: 800px;">
            <TemplateEditor
              :template="editFile.content"
              :isEditable="!editFile.is_global"
              @set="(code) => (editFile.content = code)"
            />
          </div>
        </div>
        <hr />
        <div class="px-3 py-2 flex justify-end">
          <button
            v-if="!editFile.is_global && editFile.type !== 'image' && editFile.type !== 'video'"
            class="px-3 py-1 rounded mr-3 cursor-pointer bg-gray-200"
            :class="{ 'opacity-50': editFileSaving }"
            @click="saveEditFile"
          >
            Save
          </button>
          <button
            class="px-3 py-1 rounded mr-6 cursor-pointer bg-gray-200"
            @click="closeEditFile"
          >
            Close
          </button>
        </div>
      </div>
    </vue-final-modal>
  </div>
</template>

<script>
import { api_host } from "../../constants";
import { uploadFile } from "../../service/site";
import * as assetService from "../../service/asset";
import TemplateEditor from "../TemplateEditor";
import AssetSelector from "../AssetSelector.vue";

export default {
  name: "SettingsStatic",
  emits: ["update"],
  components: {
    TemplateEditor,
    AssetSelector,
  },
  props: {
    extFiles: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      editFile: {
        key: "",
        content: "",
        is_global: false,
        type: "",
        previewUrl: "",
      },
      newDep: {
        type: "script",
        url: "",
        loading: false,
        is_global: false,
        asset_id: null,
        asset_version: null,
        asset_name: null,
        asset_original_name: null,
        asset_version_note: null,
      },
      showFileEdit: false,
      editingFile: {
        key: "",
        content: "",
      },
      editFileSaving: false,
      showVersionPicker: false,
      versionPickerLoading: false,
      versionPickerOptions: [],
      versionPickerFileRef: null,
      versionPickerAsset: null,
      versionPickerMeta: null,
      selectedVersionId: null,
    }
  },
  computed: {
    orderedExtFile() {
      const typeOrder = { script: 0, style: 1, image: 2, video: 3 };
      return [...this.extFiles].sort((a, b) => {
        const typeDiff = (typeOrder[b.type] || 999) - (typeOrder[a.type] || 999);
        return typeDiff !== 0 ? typeDiff : a.order - b.order;
      });
    },
    filesCount() {
      return {
        script: this.extFiles.filter((s) => s.type === "script").length,
        style: this.extFiles.filter((s) => s.type === "style").length,
        image: this.extFiles.filter((s) => s.type === "image").length,
        video: this.extFiles.filter((s) => s.type === "video").length,
      };
    },
    versionPickerAssetName() {
      if (this.versionPickerAsset && this.versionPickerAsset.name) {
        return this.versionPickerAsset.name;
      }
      return 'asset';
    }
  },
  methods: {
    getFileNumber(idx) {
      const file = this.orderedExtFile[idx];
      if (!file) return 1;
      
      const typeCounts = {
        script: 0,
        style: 0,
        image: 0,
        video: 0
      };
      
      for (let i = 0; i <= idx; i++) {
        const currentFile = this.orderedExtFile[i];
        if (currentFile && currentFile.type === file.type) {
          typeCounts[currentFile.type]++;
        }
      }
      
      return typeCounts[file.type];
    },
    
    updateDepFile(field, value, idx) {
      const newExtFiles = this.extFiles.map((ext, eidx) => {
        if (eidx !== idx) {
          return ext;
        }
        const updated = {
          ...ext,
          [field]: value,
        };
        if (field === 'url') {
          const isGlobalLink = value.startsWith('file:');
          updated.is_global = isGlobalLink ? ext.is_global : false;
          if (!isGlobalLink) {
            updated.asset_id = null;
            updated.asset_version = null;
            updated.asset_name = null;
            updated.asset_original_name = null;
            updated.asset_version_note = null;
          }
        }
        return updated;
      });
      this.$emit("update", { extFiles: newExtFiles });
    },
    removeDepFile(idx) {
      const clone = JSON.parse(JSON.stringify(this.extFiles));
      clone.splice(idx, 1);
      this.$emit("update", { extFiles: clone });
    },
    addDepFile() {
      this.$emit("update", {
        extFiles: this.extFiles.concat({
          id: this.extFiles.length,
          type: this.newDep.type,
          url: this.newDep.url,
          order: this.filesCount[this.newDep.type],
          is_global: !!this.newDep.is_global,
          asset_id: this.newDep.asset_id || null,
          asset_version: this.newDep.asset_version || null,
          asset_name: this.newDep.asset_name || null,
          asset_original_name: this.newDep.asset_original_name || null,
          asset_version_note: this.newDep.asset_version_note || null,
        }),
      });
      this.newDep = {
        type: "script",
        url: "",
        loading: false,
        is_global: false,
        asset_id: null,
        asset_version: null,
        asset_name: null,
        asset_original_name: null,
        asset_version_note: null,
      };
    },
    updateNewDep(newDep) {
      this.newDep = newDep;
    },
    async handleEditFile(file) {
      const url = file.url;
      const isMedia = file.type === 'image' || file.type === 'video';
      const previewUrl = url.replace("file:", `${api_host}/s3/`);
      if (isMedia) {
        this.editFile = {
          key: url,
          type: file.type,
          content: "",
          is_global: !!file.is_global,
          previewUrl,
        };
        this.showFileEdit = true;
        return;
      }
      let mimeType = "";
      const content = await fetch(previewUrl).then((res) => {
        mimeType = res.headers.get("Content-Type") || "";
        return res.text();
      });
      this.editFile = {
        key: url,
        type: mimeType,
        content,
        is_global: !!this.extFiles.find(f => f.url === url)?.is_global,
        previewUrl: "",
      };
      this.showFileEdit = true;
    },
    async saveEditFile() {
      this.editFileSaving = true;
      console.log(this.editFile.content);
      const blob = new Blob([this.editFile.content], {
        type: this.editFile.type,
      });
      const file = new File([blob], this.editFile.key, {
        type: this.editFile.type,
      });
      const res = await uploadFile(
        file,
        this.editFile.key.replace("file:", "")
      );
      console.log(res);
      this.editFileSaving = false;
      this.closeEditFile();
    },
    closeEditFile() {
      this.showFileEdit = false;
      this.editFile = { key: "", content: "" };
    },
    sortFile(direction, idx) {
      if (
        this.orderedExtFile[idx + direction].type !==
        this.orderedExtFile[idx].type
      ) {
        return;
      }
      const updatedFiles = this.orderedExtFile.map((f, fIdx) => {
        return {
          ...f,
          order:
            fIdx === idx
              ? fIdx + direction
              : fIdx === idx + direction
              ? fIdx - direction
              : f.order,
        };
      });
      this.$emit("update", {
        extFiles: updatedFiles,
      });
    },
    async openVersionPicker(file) {
      if (!file.asset_id) {
        return;
      }
      this.showVersionPicker = true;
      this.versionPickerLoading = true;
      this.versionPickerFileRef = file;
      this.versionPickerAsset = {
        name: file.asset_name || file.original_name || '',
        id: file.asset_id
      };
      try {
        const res = await assetService.getAssetVersions(file.asset_id);
        this.versionPickerOptions = (res.versions || []).map(v => ({
          ...v,
          version: v.version || 1
        }));
        this.versionPickerMeta = res.versionModel || null;
        this.selectedVersionId = file.asset_id;
      } catch (err) {
        console.error('Failed to load asset versions', err);
        this.$notify({
          type: "error",
          text: "Failed to load asset versions"
        });
        this.showVersionPicker = false;
      }
      this.versionPickerLoading = false;
    },
    closeVersionPicker() {
      this.showVersionPicker = false;
      this.versionPickerLoading = false;
      this.versionPickerOptions = [];
      this.versionPickerFileRef = null;
      this.versionPickerAsset = null;
      this.versionPickerMeta = null;
      this.selectedVersionId = null;
    },
    applySelectedVersion() {
      if (!this.versionPickerFileRef) {
        return;
      }
      const version = this.versionPickerOptions.find(v => v._id === this.selectedVersionId);
      if (!version) {
        return;
      }
      const updatedFiles = this.extFiles.map((ext) => {
        if (ext !== this.versionPickerFileRef) {
          return ext;
        }
        return {
          ...ext,
          url: `file:${version.s3_key}`,
          asset_id: version._id,
          asset_version: version.version || 1,
          asset_name: version.name || this.versionPickerAsset?.name || ext.asset_name,
          asset_original_name: version.original_name,
          asset_version_note: version.version_note || null,
          file_type: version.file_type,
          is_global: true
        };
      });
      this.$emit("update", { extFiles: updatedFiles });
      this.closeVersionPicker();
    },
    formatFileSize(bytes) {
      if (!bytes) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString();
    }
  }
}
</script>

<style scoped>
.bullet-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #4b5563;
  display: inline-block;
}

.fa-spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
